import type { MutableState } from '../../State';
import { type PromoGroupItem } from './promoGroup.types';

/**
 * Marks a group item as seen, updating item-level state.
 *
 * Per-item cooldown only: once an item is shown, that specific item is
 * blocked by its own cooldown, but other items in the group remain
 * eligible. The launch sequence picks at most one item per launch — the
 * "1 per launch" guarantee comes from the call-site (centralized block
 * returns after firing one popup), not from a group-level counter.
 *
 * @param state - Mutable state
 * @param promoItem Group and item identifiers to record.
 * @param now - Current timestamp
 * @param eventId - Current event ID for recurring items (stored as `lastShownEventId`)
 */
export function setGroupItemSeen(
  state: MutableState,
  promoItem: PromoGroupItem,
  now: number,
  eventId?: string,
): void {
  const { groupId, itemId } = promoItem;

  // Initialize group state if needed
  if (!state.promoGroups[groupId]) {
    state.promoGroups[groupId] = {
      items: {},
    };
  }

  const group = state.promoGroups[groupId];

  // Initialize item state if needed
  if (!group.items[itemId]) {
    group.items[itemId] = {
      shownAt: 0,
      count: 0,
      lastShownEventId: '',
    };
  }

  const item = group.items[itemId];

  item.shownAt = now;
  item.count++;
  if (eventId !== undefined) {
    item.lastShownEventId = eventId;
  }
}
